import { DialogueEffect, DialogueEvent } from '../types';
import { EffectHandler } from '../EffectBus';

type Fixture = {
  match: (text: string) => boolean;
  result: Omit<Extract<DialogueEvent, { type: 'PARSE_RESULT' }>, 'messageId'>;
};

const fixtures: Fixture[] = [
  // Specific patterns first (most specific to least specific)
  { match: t => /confirms.*task.*submission/i.test(t), result: { type: 'PARSE_RESULT', intentId: 'USER_CONFIRMS_TASK_SUBMISSION', slots: {}, confidence: 0.99 } },
  { match: t => /rejects.*task.*submission/i.test(t), result: { type: 'PARSE_RESULT', intentId: 'USER_REJECTS_TASK_SUBMISSION', slots: {}, confidence: 0.99 } },
  { match: t => /retypes.*task/i.test(t), result: { type: 'PARSE_RESULT', intentId: 'USER_RETYPES_TASK', slots: { task_description: 'retyped task' }, confidence: 0.9 } },
  { match: t => /submits.*tasks/i.test(t), result: { type: 'PARSE_RESULT', intentId: 'USER_SUBMITS_TASKS', slots: { task_description: 'mock tasks' }, confidence: 0.9 } },
  { match: t => /wants.*to.*submit.*task/i.test(t), result: { type: 'PARSE_RESULT', intentId: 'USER_WANTS_TO_SUBMIT_TASK', slots: {}, confidence: 0.8 } },
  { match: t => /wants.*to.*delete.*task/i.test(t), result: { type: 'PARSE_RESULT', intentId: 'USER_WANTS_TO_DELETE_TASK', slots: {}, confidence: 0.8 } },
  { match: t => /wants.*to.*change.*task/i.test(t), result: { type: 'PARSE_RESULT', intentId: 'USER_WANTS_TO_CHANGE_TASK', slots: {}, confidence: 0.8 } },
  { match: t => /accepts.*all.*tasks/i.test(t), result: { type: 'PARSE_RESULT', intentId: 'USER_ACCEPTS_ALL_TASKS', slots: {}, confidence: 0.99 } },
  { match: t => /cancels.*deletion/i.test(t), result: { type: 'PARSE_RESULT', intentId: 'USER_CANCELS_DELETION', slots: {}, confidence: 0.99 } },
  { match: t => /cancels.*change/i.test(t), result: { type: 'PARSE_RESULT', intentId: 'USER_CANCELS_CHANGE', slots: {}, confidence: 0.99 } },
  { match: t => /all.*tasks.*completed/i.test(t), result: { type: 'PARSE_RESULT', intentId: 'ALL_TASKS_COMPLETED', slots: {}, confidence: 0.99 } },
  
  // Common patterns
  { match: t => /says.*no/i.test(t), result: { type: 'PARSE_RESULT', intentId: 'USER_SAYS_NO', slots: {}, confidence: 0.99 } },
  { match: t => /says.*goodbye/i.test(t), result: { type: 'PARSE_RESULT', intentId: 'USER_SAYS_GOODBYE', slots: {}, confidence: 0.99 } },
  { match: t => /says.*hello/i.test(t), result: { type: 'PARSE_RESULT', intentId: 'USER_SAYS_HELLO', slots: {}, confidence: 0.99 } },
  { match: t => /says.*something.*else/i.test(t), result: { type: 'PARSE_RESULT', intentId: 'USER_SAYS_SOMETHING_ELSE', slots: {}, confidence: 0.7 } },
  { match: t => /asks.*for.*help/i.test(t), result: { type: 'PARSE_RESULT', intentId: 'USER_ASKS_FOR_HELP', slots: {}, confidence: 0.99 } },
  
  // Generic patterns (less specific)
  { match: t => /confirms/i.test(t), result: { type: 'PARSE_RESULT', intentId: 'USER_CONFIRMS', slots: {}, confidence: 0.99 } },
  { match: t => /rejects/i.test(t), result: { type: 'PARSE_RESULT', intentId: 'USER_REJECTS', slots: {}, confidence: 0.99 } },
  { match: t => /submits/i.test(t), result: { type: 'PARSE_RESULT', intentId: 'USER_SUBMITS_TASK', slots: { task_description: 'mock task' }, confidence: 0.9 } },
  
  // Fallback patterns
  { match: t => /hello|hi/i.test(t), result: { type: 'PARSE_RESULT', intentId: 'USER_SAYS_HELLO', slots: {}, confidence: 0.99 } },
  { match: t => /bye|goodbye/i.test(t), result: { type: 'PARSE_RESULT', intentId: 'USER_SAYS_GOODBYE', slots: {}, confidence: 0.99 } },
  { match: t => /task/i.test(t), result: { type: 'PARSE_RESULT', intentId: 'USER_SUBMITS_TASK', slots: { task_description: 'mock task' }, confidence: 0.9 } },
];

// Task dialogue fixtures (map State Walker button labels to task intents)
const taskFixtures: Fixture[] = [
  // processing_options_selection
  { match: t => /select\s+option/i.test(t), result: { type: 'PARSE_RESULT', intentId: 'SELECT_OPTION', slots: {}, confidence: 0.99 } },
  { match: t => /invalid\s+option/i.test(t), result: { type: 'PARSE_RESULT', intentId: 'INVALID_OPTION', slots: {}, confidence: 0.99 } },
  { match: t => /rephrase\s+task/i.test(t), result: { type: 'PARSE_RESULT', intentId: 'REPHRASE_TASK', slots: { task_description: 'rephrased task' }, confidence: 0.99 } },
  { match: t => /no\s+tasks\s+found/i.test(t), result: { type: 'PARSE_RESULT', intentId: 'NO_TASKS_FOUND', slots: {}, confidence: 0.99 } },

  // common across task states
  { match: t => /^abort$/i.test(t) || /\babort\b/i.test(t), result: { type: 'PARSE_RESULT', intentId: 'ABORT', slots: {}, confidence: 0.99 } },
  { match: t => /something\s+else/i.test(t), result: { type: 'PARSE_RESULT', intentId: 'SOMETHING_ELSE', slots: {}, confidence: 0.9 } },

  // variant_selection
  { match: t => /select\s+variant/i.test(t), result: { type: 'PARSE_RESULT', intentId: 'SELECT_VARIANT', slots: { variant: 'variant-1' }, confidence: 0.99 } },
  { match: t => /invalid\s+variant/i.test(t), result: { type: 'PARSE_RESULT', intentId: 'INVALID_VARIANT', slots: {}, confidence: 0.99 } },
  { match: t => /rephrase\s+variant/i.test(t), result: { type: 'PARSE_RESULT', intentId: 'REPHRASE_VARIANT', slots: { variant: 'variant-1' }, confidence: 0.99 } },

  // amount_clarification
  { match: t => /enter\s+amount/i.test(t), result: { type: 'PARSE_RESULT', intentId: 'ENTER_AMOUNT', slots: { amount: '5' }, confidence: 0.99 } },
  { match: t => /invalid\s+amount/i.test(t), result: { type: 'PARSE_RESULT', intentId: 'INVALID_AMOUNT', slots: {}, confidence: 0.99 } },

  // amount → task_completed (assignment location)
  { match: t => /enter\s+location/i.test(t), result: { type: 'PARSE_RESULT', intentId: 'ENTER_LOCATION', slots: { location: 'Amsterdam' }, confidence: 0.99 } },

  // abort_confirmation
  { match: t => /^confirm$/i.test(t) || /\bconfirm\b/i.test(t), result: { type: 'PARSE_RESULT', intentId: 'CONFIRM', slots: { confirmation: true }, confidence: 0.99 } },
];

export class MockServiceEffectsHandler implements EffectHandler {
  private dispatch: (event: DialogueEvent) => void;
  private log: (entry: string) => void;

  constructor(dispatch: (event: DialogueEvent) => void, log: (entry: string) => void) {
    this.dispatch = dispatch;
    this.log = log;
  }

  canHandle(effect: DialogueEffect): boolean {
    return effect.type === 'CALL_LLM_PARSE';
  }

  async handle(effect: DialogueEffect): Promise<void> {
    if (effect.type !== 'CALL_LLM_PARSE') return;
    const { text, messageId } = effect;
    try {
      const fx = fixtures.find(f => f.match(text)) || taskFixtures.find(f => f.match(text));
      if (!fx) {
        this.log('PARSE_ERROR: no matching fixture');
        this.dispatch({ type: 'PARSE_ERROR', messageId, reason: 'no_fixture' });
        return;
      }
      this.log(`PARSE_RESULT from fixture: intentId=${fx.result.intentId}`);
      this.dispatch({ ...fx.result, messageId });
    } catch (e) {
      this.log('PARSE_ERROR: exception');
      this.dispatch({ type: 'PARSE_ERROR', messageId, reason: 'exception' });
    }
  }
}


